import React from 'react'
import type { UseMention, UseMentionArgs, UserOption } from '../constants'
import styles from '../index.module.scss'

export const useMention = ({
  trigger = '@', // DO NOT REMOVE
  optionsRef,
  getOptions, // DO NOT REMOVE
  handleReset,
  handleSubmit,
}: UseMentionArgs & { handleReset: () => void; handleSubmit: () => void }): UseMention => {
  // Flag to track if the system is in a "just selected" state (for Enter key logic)
  const mentionSelectedRef = React.useRef(false)

  const setupAutocompleter = (editor: any) => {
    editor.ui.registry.addAutocompleter('mentions', {
      // The character that triggers the dropdown
      trigger,

      // This is crucial: 0 means the menu appears immediately after typing '@'
      minChars: 0,

      // Function to dynamically fetch and filter the list
      fetch: async (pattern: string) => {
        // Skip search if space is entered (Correct)
        if (pattern.includes(' ')) {
          optionsRef.current = [] as UserOption[]
          return []
        }

        // Fetch options using the external function
        const newOptions = (await getOptions(pattern)) as UserOption[]
        optionsRef.current = newOptions

        // Format the options for TinyMCE's Autocompleter API (CORRECTED FORMAT)
        const formattedOptions = newOptions.map((item) => {
          return {
            // The 'value' is inserted into the editor via onAction
            value: item._id,

            // The 'text' is the standard display name (required)
            text: `${item.profile.name} (${item.profile.email})`,

            // 'icon' is optional but good for standard menu items
            icon: 'user',

            // The card structure for richer display
            type: 'cardmenuitem',
            items: [
              {
                type: 'cardcontainer',
                direction: 'horizontal',
                items: [
                  {
                    type: 'cardimage',
                    src: item.profile?.picture
                      ? item.profile?.picture
                      : 'https://res.cloudinary.com/tmmshauler/image/upload/v1506318907/hauler/dft-customer.jpg',
                    classes: ['p-1', `${styles.activiyMentionImg}`],
                  },
                  {
                    type: 'cardtext',
                    text: `${item.profile.name} (${item.profile.email})`,
                    name: 'char_name',
                  },
                ],
              },
            ],
          }
        })

        // Return the formatted array.
        return formattedOptions
      },

      // Function called when a user selects an item from the dropdown
      onAction: (autocompleteApi: any, rng: Range, value: string) => {
        const user = optionsRef.current.find((u) => u._id === value)

        if (!user) {
          autocompleteApi.hide()
          return
        }

        // Set the flag to block the immediate "ghost" Enter keydown event
        //This will determine if the event is from mention select
        mentionSelectedRef.current = true

        // Construct the **non-editable** HTML for the mention tag.
        const mentionHtml = `<b><span 
            class="mention-tag" 
            data-user-id="${user._id}" 
            contenteditable="false" 
            title="${user?.profile?.email}"
          >@${user?.profile?.name}</span></b>&nbsp;`

        //Insert the HTML into the editor, replacing the trigger text (RESTORED)
        editor.selection.setRng(rng)
        editor.insertContent(mentionHtml)

        // Close the autocomplete menu
        autocompleteApi.hide()

        // Clear the flag after a short delay (e.g., 100ms) to unblock the submission.
        setTimeout(() => {
          mentionSelectedRef.current = false
        }, 100)
      },
    })

    editor.on('KeyUp', (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleReset()

      if (e.key === 'Enter' && !e.shiftKey) {
        // Stop all propagation immediately to prevent any other handlers (including TinyMCE's internal ones) from running.
        e.stopImmediatePropagation()
        e.preventDefault()

        //from the onAction, it will be true,
        //it should not submit if from the selection of mentions
        if (mentionSelectedRef.current) {
          e.preventDefault()
          return // onAction's timeout will clear the ref.
        }

        // Remove any ghost line breaks that TinyMCE might buffer
        editor.execCommand('Delete')

        // Use a tiny timeout (10ms) to ensure the event loop is stable before submitting.
        setTimeout(() => {
          handleSubmit()
        }, 10)
      }
    })

    editor.on('Init', () => {
      const editorContainer = editor.getContainer()
      if (editorContainer) {
        editorContainer.classList.add(styles.flatEditorContainer)
      }

      const editAreaContainer = editor.getContainer()?.querySelector('.tox-edit-area')

      if (editAreaContainer) {
        editAreaContainer.classList.add(styles.activeEditor)
      }
    })
  }

  const placeholder = 'Type @ to mention someone. Press "Escape" key to cancel or "Enter" to save.'

  return { setupAutocompleter, placeholder }
}
